import { StyleSheet } from 'aphrodite';
import { white, charmGray, borderOutline } from '../../../styles/colors';

/**
 * Branch Colors for Conditional Paths
 *
 * These colors are carefully chosen for use in conditional branch edges.
 * They remain as hardcoded constants rather than centralized theme colors because:
 *
 * 1. **Visual Distinction**: Each branch needs a unique, distinguishable color to help
 *    users trace different conditional paths through the automation flow.
 *
 * 2. **Accessibility**: This palette is designed to be colorblind-friendly and provides
 *    sufficient contrast against white backgrounds (WCAG AA compliant).
 *
 * 3. **Semantic Stability**: These colors map to specific case indices and should remain
 *    consistent across sessions to avoid confusion when users revisit automations.
 *
 * 4. **Design System Independence**: Branch colors serve a specialized data visualization
 *    purpose distinct from brand/UI colors, similar to chart/graph color palettes.
 *
 * If modifying these colors, ensure:
 * - Minimum contrast ratio of 4.5:1 against white background
 * - Colors are distinguishable for users with color vision deficiencies
 * - Total count supports the maximum number of conditional branches
 */
export const BRANCH_COLORS = [
	'#3B82F6', // Blue - Case 1 (Primary/Default)
	'#10B981', // Green - Case 2 (Success/Positive)
	'#F59E0B', // Amber - Case 3 (Warning/Caution)
	'#EF4444', // Red - Case 4 (Error/Negative)
	'#8B5CF6', // Purple - Case 5
	'#EC4899', // Pink - Case 6
	'#14B8A6', // Teal - Case 7
	'#F97316', // Orange - Case 8
] as const;

/**
 * Color used for the "default" / "else" case in conditional branches.
 * Gray to distinguish from the colored conditional branches.
 */
export const DEFAULT_BRANCH_COLOR = '#6B7280';

export const styleSheet = StyleSheet.create({
	labelContainer: {
		position: 'absolute',
		pointerEvents: 'all',
		cursor: 'pointer',
		zIndex: 1000,
	},

	labelBadge: {
		padding: '6px 10px',
		color: white,
		borderRadius: 6,
		fontSize: 11,
		fontWeight: 600,
		whiteSpace: 'nowrap',
		maxWidth: 250,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
		border: '1px solid rgba(255,255,255,0.2)',
	},

	tooltip: {
		position: 'absolute',
		top: '100%',
		left: '50%',
		marginTop: 8,
		padding: 12,
		backgroundColor: white,
		border: `1px solid ${borderOutline}`,
		borderRadius: 6,
		boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
		zIndex: 1000,
		minWidth: 250,
		maxWidth: 400,
	},

	tooltipHeader: {
		fontSize: 11,
		fontWeight: 600,
		color: charmGray,
		marginBottom: 8,
	},
});

// Backwards compatible alias
export const branchEdgeStyles = styleSheet;
